import * as React from 'react';
import { renderToString } from 'react-dom/server';

import { convertArrayToCSV } from 'convert-array-to-csv';
import { EChartsInstance } from 'echarts-for-react';
import SaveAsImage from 'echarts/lib/component/toolbox/feature/SaveAsImage';
import { LegendOption } from 'echarts/types/dist/shared';
import { GraphicComponentTextOption } from 'echarts/types/src/component/graphic/GraphicModel';
import FileSaver from 'file-saver';
import { flatten, groupBy, isArray, isEqual, isFunction, merge, uniq, uniqBy } from 'lodash/fp';

import { EChartsWrapper } from '../../components';
import _ from '../../locale';
import {
    DAY_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    START_DATE_KEY,
    VALUE_KEY,
    YEAR_KEY,
} from '../../models/data-key';
import { styled } from '../../theme';
import echartsTheme from '../../theme/echarts-theme';
import { csvIcon } from '../../theme/icons';
import {
    DataFilters,
    DataGranularityKey,
    DataKey,
    DataRecord,
    DataResponse,
    TimeUnitKey,
} from '../../types';
import { localizeFilterValues } from '../../utility/chart-data-helpers';
import { GHT_LEVEL_COLORS, rgbaToRgb } from '../../utility/color';
import getTitleAndSubtitle, {
    getAdoptedTextAndStyles,
} from '../../utility/echarts-helpers/get-adopted-title-and-subtitle';
import {
    getLegendHeight,
    getTitlesHeight,
} from '../../utility/echarts-helpers/get-chart-component-height';
import getSelectedTimeUnitsRange from '../../utility/echarts-helpers/get-selected-time-units-range';
import { measureTextSize } from '../../utility/echarts-helpers/text-utils';
import { formatValue } from '../../utility/filters-helpers';
import formatChartValue from '../../utility/formatting/formatChartValue';
import Tooltip from './Tooltip';

const styles = {
    echarts: {
        width: '100%',
        paddingTop: 20,
        marginBottom: 20,
    },
};

const ChartWrapper = styled('div')({
    display: 'flex',
});

const COMPACT_VIEW_BREAKPOINT = 680;
const CHART_VIEW_HEIGHT = 370;
const PAGINATED_LEGEND_HEIGHT = 260;
const TITLE_PADDING = 15;
const SLIDER_HEIGHT = 20;

const cursorCoords = {
    y: 0,
};

const defaultProps = {
    height: CHART_VIEW_HEIGHT,
    withSlider: false,
    withLegend: true,
    isLocalizedData: false,
    xAxisKey: YEAR_KEY,
    renderXAxisTitle: () => '',
    markLines: [],

    yAxisKey: VALUE_KEY,
    areaKeys: [],
    renderYAxisTitle: () => '',
    xAxisLabelFormatter: (value: string) => value,
    yAxisLabelFormatter: (value) => value,
    yAxisMaxValue: -1,
    groupByKeys: [],

    renderTitle: () => '',
    renderSubtitle: () => '',

    lineParams: [],

    saveAsImage: {
        visible: false,
        filename: () => 'Chart_ScreenShot',
        icon: '',
        title: '',
    },
    saveAsCSV: {
        visible: false,
        filename: () => 'Chart_ScreenShot',
        isLocalizedData: false,
        icon: '',
        title: '',
        headers: [],
        keys: [],
    },
    onEvents: {},

    animation: true,
    legendPagination: false,
    maxValuesSelectedFilter: PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    renderNoDataMessage: () => _('chart_error_no_data'),
    subheadingHeight: 0,
};

type Props = {
    isLoadingData: boolean;
    filtersValues: DataFilters;
    columns: Array<DataKey>;
    records: Array<DataRecord>;

    height: number;
    withSlider: boolean;
    withLegend?: boolean;
    isLocalizedData?: boolean;
    onEvents: object;
    xAxisKey: TimeUnitKey;
    areaKeys: Array<string>;
    renderXAxisTitle: () => void;
    markLines: {
        isHorizontal?: boolean;
        label: string;
        value: number | string;
        color: number | string;
    }[];
    yAxisKey: DataKey;
    renderYAxisTitle: () => void;
    yAxisLabelFormatter: () => void;
    numberFormatter: (value: number | string) => string;

    getSelectedSeries: (value: string[]) => object;
    yAxisMaxValue: number;
    groupByKeys: string[];

    lineParams: Array<{ color: string }>;

    saveAsImage: {
        visible?: boolean;
        enabled?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        onClick?: () => void;
    };
    saveAsCSV: {
        visible?: boolean;
        filename?: () => void;
        icon?: string;
        title?: string;
        isLocalizedData?: boolean;
        headers?: Array<string> | (() => string);
        keys?: Array<string> | (() => string);
    };

    sortRecords?: (a: any, b: any) => number;
    renderTooltip?: () => void;
    renderTitle?: () => void;
    renderSubtitle?: () => void;
    renderNoDataMessage?: () => void;
    onChartResize?: (width: number) => void;

    formatSeriesName?: (groupIdx, key, values: any) => string;
    generateLineParams?: (record: DataRecord) => object;
    handleDataZoomChange?: (timeRange: { start: string; end: string }) => void;

    animation: boolean;
    legendPagination: boolean;
    maxValuesSelectedFilter: DataGranularityKey;

    isHeatmapEnabled: boolean;
    heatmapDataResponse?: DataResponse | null;
    subheadingHeight?: number;
};

type State = {
    zoomStartValue: number;
    zoomEndValue: number;
    zoomStartIdx: number;
    zoomEndIdx: number;
    chartWidth: number;
    legendHeight: number;
    titlesHeight: number;
    extraTitlesHeight: number;
    xAxisValues: (string | number)[];
    showHeatmapLabels: boolean;
};

class LineChart extends React.PureComponent<Props, State> {
    static defaultProps = defaultProps;

    theme?: typeof echartsTheme;
    chartInstance = null;

    constructor(props) {
        super(props);

        this.state = {
            zoomStartValue: 0,
            zoomEndValue: 100,
            zoomStartIdx: 0,
            zoomEndIdx: 0,
            chartWidth: window.innerWidth,
            legendHeight: 0,
            titlesHeight: 0,
            extraTitlesHeight: 0,
            xAxisValues: [],
        };
        this.theme = merge(echartsTheme, props.theme);
    }

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any
    ): void {
        const { isHeatmapEnabled, isLoadingData } = this.props;

        const newState: Partial<State> = {};
        if (isHeatmapEnabled) {
            newState.showHeatmapLabels =
                this.chartInstance?._chartsViews?.find(({ type }) => type === 'heatmap')?.group
                    ?._children[0]?.shape?.width > 45;
        }

        const titlesHeight = getTitlesHeight(this.chartInstance);
        if (titlesHeight !== 0) {
            newState.titlesHeight = titlesHeight;
        }

        const legendHeight = getLegendHeight(this.chartInstance, this.getLegendY());
        if (legendHeight !== 0) {
            newState.legendHeight = legendHeight;
        }

        if (isLoadingData) {
            newState.zoomStartValue = 0;
            newState.zoomEndValue = 100;
        }

        const xAxisValues = this.getXAxisData();
        let areXAxisValuesChanged = !isEqual(xAxisValues, prevState.xAxisValues);

        if (
            (prevProps.isLoadingData && !isLoadingData && this.chartInstance) ||
            prevProps.isHeatmapEnabled !== isHeatmapEnabled ||
            areXAxisValuesChanged
        ) {
            const { start, end } = this.getZoomIndexes();
            newState.zoomStartIdx = start;
            newState.zoomEndIdx = end;
        }

        if (areXAxisValuesChanged) {
            newState.xAxisValues = this.getXAxisData();
            this.chartInstance.clear();
        }

        if (
            (legendHeight && prevState.legendHeight !== newState.legendHeight) ||
            (newState.titlesHeight && prevState.titlesHeight !== newState.titlesHeight) ||
            prevState.extraTitlesHeight !== newState.extraTitlesHeight ||
            (!prevProps.isLoadingData && isLoadingData) ||
            prevState.showHeatmapLabels !== newState.showHeatmapLabels ||
            prevProps.isHeatmapEnabled !== isHeatmapEnabled ||
            areXAxisValuesChanged
        ) {
            this.setState(newState);
        }
    }

    componentWillUnmount(): void {
        this.chartInstance.getZr().off('mousemove', this.setCursorPosition);
    }

    onChartReady = (instance: EChartsInstance) => {
        this.chartInstance = instance;
        this.chartInstance.getZr().on('mousemove', this.setCursorPosition);
    };

    onChartResize = (width: number, height: number) => {
        this.setState({ chartWidth: width });
        this.props.onChartResize?.(width);
    };

    resetZoom = () =>
        this.setState({
            zoomStartValue: 0,
            zoomEndValue: 100,
        });

    getXAxisData = () => {
        const { columns, records, xAxisKey, isHeatmapEnabled, heatmapDataResponse, sortRecords } =
            this.props;
        if (!columns.includes(xAxisKey)) {
            return [];
        }

        const idx = columns.indexOf(xAxisKey);
        return uniq(
            [...records, ...(isHeatmapEnabled ? heatmapDataResponse?.records : [])].map(
                (record) => record[idx]
            )
        ).sort(sortRecords);
    };

    getToolboxFeatures = () => {
        const features: any = {
            saveAsImage: { show: false },
        };

        const { saveAsImage, saveAsCSV } = this.props;

        if (saveAsImage && saveAsImage.visible) {
            features.mySaveAsImage = this.getSaveAsImageConfig();
        }

        if (saveAsCSV && saveAsCSV.visible) {
            features.mySaveAsCSV = this.getSaveAsCSVConfig();
        }

        return features;
    };

    getSaveAsImageConfig = () => {
        const {
            visible,
            enabled,
            filename = (renderProps?) => 'Chart_ScreenShot',
            icon,
            title = _('chart_save_as_image_button_label'),
            onClick,
        } = this.props.saveAsImage;

        if (!visible) {
            return undefined;
        }

        const config = {
            excludeComponents: ['toolbox'],
            pixelRatio: 2,
            title,
            name: filename(this.getRenderProps()),
            onclick: function (...params) {
                if (!enabled) {
                    return;
                }
                SaveAsImage.prototype.onclick.call(this, ...params);
                onClick && onClick();
            },
            ...(!enabled && {
                iconStyle: {
                    color: '#A2A3A4',
                },
                emphasis: {
                    iconStyle: {
                        color: '#A2A3A4',
                    },
                },
            }),
        };

        return icon ? { ...config, icon } : config;
    };

    addSaveAsImageOverlap = (): object | null => {
        const { visible, enabled } = this.props.saveAsImage;

        return visible && !enabled
            ? {
                  type: 'rect',
                  cursor: 'default',
                  z: 1000,
                  top: 20,
                  right: 30,
                  shape: {
                      width: 30,
                      height: 40,
                  },
                  style: {
                      fill: 'rgba(100,100,100,0)',
                  },
              }
            : null;
    };

    getSaveAsCSVConfig = () => {
        const {
            visible,
            filename,
            icon,
            title = _('chart_save_as_csv_button_label'),
        } = this.props.saveAsCSV;

        if (!visible) {
            return undefined;
        }

        return {
            icon: icon || csvIcon,
            title,
            onclick: () => {
                const name = filename(this.getRenderProps()) + '.csv';
                const data = new Blob([this.getCSVData()], {
                    type: 'text/plain',
                });
                FileSaver.saveAs(data, name);
            },
        };
    };

    getCSVData = () => {
        const { columns, records, saveAsCSV } = this.props;

        // values without suffix '_id' shouldn't be translated using locale
        const formatValueByKey = (value, key) =>
            key.endsWith('_id') ? _(key.replace('_id', '_') + value) : value;

        const columnHeaders = isFunction(saveAsCSV.headers)
            ? saveAsCSV.headers.call(this, this.getRenderProps())
            : saveAsCSV.headers;

        const columnsKeys = isFunction(saveAsCSV.keys)
            ? saveAsCSV.keys.call(this, this.getRenderProps())
            : saveAsCSV.keys;

        const arrayRecords = records.map((record) => [
            ...columnsKeys.map((key) =>
                saveAsCSV.isLocalizedData
                    ? record[columns.indexOf(key)]
                    : formatValueByKey(record[columns.indexOf(key)], key)
            ),
        ]);

        return convertArrayToCSV(arrayRecords, { header: columnHeaders });
    };

    getRenderProps = () => {
        const { filtersValues, xAxisKey } = this.props;
        const { zoomStartIdx, zoomEndIdx, xAxisValues, titlesHeight } = this.state;

        return {
            filters: filtersValues,
            timeRange: getSelectedTimeUnitsRange(
                zoomStartIdx,
                zoomEndIdx,
                xAxisValues,
                xAxisKey,
                _
            ),
            isMobileView: this.isMobileView(),
            titlesHeight,
        };
    };

    getDataZoomConfig = () => {
        const { xAxisKey, withSlider, height } = this.props;
        const { zoomStartValue, zoomEndValue } = this.state;

        return [
            {
                type: 'slider',
                show: withSlider,
                start: zoomStartValue,
                end: zoomEndValue,
                ...this.theme.dataZoom,
                top: this.getGridY() + height + 30,
                ...([DAY_KEY, START_DATE_KEY].includes(xAxisKey) && {
                    right: 85,
                    left: 80,
                }),
            },
        ];
    };

    onDataZoomChange = ({ start, end }, echartsInstance: EChartsInstance) => {
        const { start: zoomStartIdx, end: zoomEndIdx } = this.getZoomIndexes();
        const { xAxisValues } = this.state;
        const { xAxisKey, handleDataZoomChange } = this.props;

        this.setState(
            { zoomStartValue: start, zoomEndValue: end, zoomStartIdx, zoomEndIdx },
            () => {
                handleDataZoomChange?.(
                    getSelectedTimeUnitsRange(zoomStartIdx, zoomEndIdx, xAxisValues, xAxisKey, _)
                );
            }
        );
    };

    getZoomIndexes = (echartsInstance?: EChartsInstance) => {
        let start = 0;
        let end = Math.max(0, this.getXAxisData().length - 1);

        const dataZoomOption = (echartsInstance || this.chartInstance)?.getOption()?.dataZoom?.[0];
        if (dataZoomOption) {
            start = dataZoomOption.startValue;
            end = dataZoomOption.endValue;
        }

        return { start, end };
    };

    isMobileView = () => this.state.chartWidth < COMPACT_VIEW_BREAKPOINT;

    getPointsClosestToCursor = (params) => {
        if (!isArray(params)) {
            return [params];
        }
        const { yAxisKey } = this.props;
        const valueIdx = params[0].dimensionNames.findIndex((name) => name === yAxisKey);
        const valueItems = params.filter((item) => item?.encode?.y?.[0] === valueIdx);
        const itemsByCoords = valueItems.map((item) => {
            const { axisId, axisIndex, seriesId, seriesIndex, seriesName, encode, data } = item;
            const value = [data[encode.x], data[encode.y]];
            const coords = this.chartInstance.convertToPixel(
                {
                    seriesIndex,
                    seriesId,
                    seriesName,
                    xAxisId: axisId,
                    xAxisIndex: axisIndex,
                },
                value
            );

            return [coords[1], item];
        });
        const sortedItems = itemsByCoords.sort(
            (a, b) => Math.abs(cursorCoords.y - +a[0]) - Math.abs(cursorCoords.y - +b[0])
        );

        return sortedItems.map(([coords, item]) => item);
    };

    getSeriesName = (record: DataRecord, idx: number, omitFilter: DataKey | null = null) =>
        this.props?.formatSeriesName ? this.props.formatSeriesName(record, omitFilter) : idx;

    renderTooltip = (params) => {
        const { numberFormatter } = this.props;

        return (
            this.props.renderTooltip?.(params) ||
            renderToString(
                <Tooltip
                    items={this.getPointsClosestToCursor(params)}
                    numberFormatter={numberFormatter}
                />
            )
        );
    };

    formatXAxisLabel = (value) =>
        this.props.xAxisLabelFormatter?.(value) || formatValue(this.props.xAxisKey, value, _);

    getChartOption = () => {
        const {
            isLoadingData,
            areaKeys,
            renderTitle,
            renderSubtitle,
            renderXAxisTitle,
            renderYAxisTitle,
            xAxisKey,
            yAxisLabelFormatter,
            animation,
            columns,
            isHeatmapEnabled,
            heatmapDataResponse,
            renderNoDataMessage,
            subheadingHeight,
        } = this.props;

        const { chartWidth, showHeatmapLabels } = this.state;

        const dataBySeries = this.breakDataBySeries();

        const showArea = areaKeys && areaKeys.length && areaKeys.length > 1;
        const series = this.getSeries(dataBySeries, showArea);
        const { legends, titles: legendTitles } = this.getLegends(dataBySeries);
        const canvasFontSizer = document.getElementById('canvasFontSizer');

        const option = {
            ...this.theme.line,
            title: [
                getTitleAndSubtitle(
                    renderTitle(this.getRenderProps()),
                    renderSubtitle(this.getRenderProps()),
                    chartWidth,
                    canvasFontSizer,
                    { ...this.theme.title, backgroundColor: null, top: `${subheadingHeight}px` }
                ),
                {
                    show: !(isLoadingData || (dataBySeries && dataBySeries.length)),
                    ...getAdoptedTextAndStyles(
                        renderNoDataMessage(this.getRenderProps()),
                        chartWidth - 140,
                        canvasFontSizer,
                        {
                            ...this.theme.title.textStyle,
                            fontSize: 28,
                        },
                        20
                    ),
                    top: 190,
                },
            ],
            tooltip: {
                formatter: this.renderTooltip,
                ...this.theme.line.tooltip,
            },
            toolbox: {
                top: 20,
                right: 30,
                z: 1000,
                ...this.theme.toolbox,
                feature: this.getToolboxFeatures(),
            },
            grid: {
                ...this.theme.line.grid,
                top: this.getGridY(),
                height: this.props.height,
                right: 60,
            },
            xAxis: {
                type: 'category',
                ...this.theme.xAxis,
                name: renderXAxisTitle(this.getRenderProps()),
                nameGap: 70,
                axisLabel: {
                    ...this.theme.xAxis.axisLabel,
                    ...(this.theme.line?.xAxis?.axisLabel && this.theme.line?.xAxis?.axisLabel),
                    formatter: this.formatXAxisLabel,
                },
                data: this.getXAxisData(),
            },
            yAxis: [
                {
                    ...this.theme.yAxis,
                    axisLabel: {
                        ...this.theme.yAxis.axisLabel,
                        formatter: yAxisLabelFormatter,
                    },
                    name: renderYAxisTitle(this.getRenderProps()),
                },
            ],
            dataZoom: this.getDataZoomConfig(),
            legend: legends,
            dataset: this.getDatasets(dataBySeries, showArea),
            series,
            animation,
            graphic: { elements: [this.addSaveAsImageOverlap(), ...legendTitles] },
        };

        if (isHeatmapEnabled) {
            const startDateIdx = columns.indexOf(xAxisKey);

            const { records: heatmapRecords } = heatmapDataResponse;

            const levels = columns.filter((key) => key.includes('prob_level'));
            const levelIdxs = levels.map((level) => columns.indexOf(level));

            const groupedHeatmapRecords = Object.values(
                groupBy(
                    1,
                    flatten(
                        heatmapRecords.map((record) => {
                            const startDate = record[startDateIdx];

                            return record
                                .filter((value, idx) => levelIdxs.includes(idx))
                                .map((value, idx) => {
                                    const numValue = Number(value);
                                    const [r, g, b] = GHT_LEVEL_COLORS[idx];
                                    const a = Math.max(numValue, 0.1);
                                    const color = rgbaToRgb(r, g, b, a);
                                    return [startDate, levels[idx], numValue, color];
                                });
                        })
                    )
                )
            );

            const dataset = groupedHeatmapRecords
                .map((records) => ({
                    dimensions: ['x', 'y', 'value', 'color'],
                    source: records,
                }))
                .reverse();

            const maxValue = 8;

            option.title.push({
                show: true,
                text: 'Highest to Lowest ↓',
                background: 'none',
                right: 0,
                top: 60,
                textStyle: {
                    ...echartsTheme.textStyle,
                    fontSize: 11,
                },
            });
            option.dataset.push(dataset);
            option['visualMap'] = {
                show: false,
                min: 0,
                max: 1,
                calculable: true,
            };
            option.yAxis[0].max = maxValue;
            option.yAxis.push({
                type: 'category',
                axisLine: {
                    show: false,
                    lineStyle: {
                        color: '#ECEDED',
                        width: 2,
                    },
                },
                axisTick: {
                    length: 70,
                    lineStyle: {
                        color: '#ECEDED',
                        type: 'dashed',
                        width: 1,
                    },
                },
                axisLabel: {
                    ...this.theme.yAxis.axisLabel,
                    formatter: (key) => _(key),
                },
            });
            option.series.push(
                ...dataset.map(({ source }) => ({
                    type: 'heatmap',
                    z: 100,
                    yAxisIndex: 1,
                    data: source.map(([x, y, value, color]) => ({
                        value: [x, y, value],
                        itemStyle: { color },
                        emphasis: {
                            itemStyle: {
                                color,
                                borderWidth: 0,
                            },
                        },
                    })),
                    label: {
                        show: showHeatmapLabels,
                        formatter: (params) => `${formatChartValue(params.value[2] * 100)}%`,
                    },
                    itemStyle: {
                        borderColor: '#DFE0E0',
                        borderWidth: 1,
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                    tooltip: {
                        trigger: 'item',
                    },
                }))
            );
        }

        return option;
    };

    getLegends = (
        dataBySeries
    ): { legends: LegendOption[]; titles: GraphicComponentTextOption[] } => {
        const { withLegend, columns, maxValuesSelectedFilter, legendPagination, isLocalizedData } =
            this.props;
        const { chartWidth } = this.state;
        const groupLegendsBy = maxValuesSelectedFilter;
        const groupLegendsByIdx = columns.indexOf(groupLegendsBy);
        const legendGroupGap = 30;
        const containerX = this.theme.line.grid.left;
        const containerWidth =
            chartWidth - containerX - this.theme.line.grid.right + legendGroupGap;

        const seriesInfo = uniqBy(
            'name',
            dataBySeries.map((records, idx) => {
                const name = this.getSeriesName(records[0], idx);
                const displayName = this.getSeriesName(records[0], idx, groupLegendsBy);
                return {
                    name,
                    displayName: displayName || name,
                    record: records[0],
                    idx,
                };
            })
        );

        const legendNamesMap = {};
        seriesInfo.forEach(({ name, displayName }) => {
            legendNamesMap[name] = displayName;
        });

        const groupedSeries = groupBy((item) => item.record[groupLegendsByIdx], seriesInfo);

        const minGroupWidth = 250;
        const groupsAmount: number = Object.keys(groupedSeries).length;
        const maxGroupsAmountInARow: number = Math.floor(containerWidth / minGroupWidth);
        const groupsInARow = Math.min(groupsAmount, maxGroupsAmountInARow);

        const legendItemWidth = Math.floor(containerWidth / groupsInARow);
        const groupedSeriesArr = Object.entries(groupedSeries);
        const legendData = groupedSeriesArr.map(([key, items]) => {
            const width = legendItemWidth;
            const localizedName = !isLocalizedData
                ? localizeFilterValues(groupLegendsBy, [key])
                : name;
            const showGroupTitle = !(
                groupedSeriesArr.length === 1 ||
                (items.length === 1 && items[0]['name'] === localizedName)
            );
            const nameHeight = measureTextSize(
                localizedName,
                this.theme?.line.legend.titleTextStyle,
                width
            ).height;
            const itemsOffsetY = showGroupTitle ? nameHeight + 12 : 0;
            let height = items.reduce(
                (acc: number, item) =>
                    acc +
                    measureTextSize(
                        item?.displayName?.toString() || '',
                        this.theme?.line.legend.textStyle,
                        legendItemWidth - 30 - legendGroupGap
                    ).height +
                    8,
                itemsOffsetY
            );
            if (legendPagination) {
                height = Math.min(height, PAGINATED_LEGEND_HEIGHT);
            }

            return {
                name: localizedName,
                items,
                height,
                width,
                x: 0,
                y: 0,
                itemsOffsetY,
                showGroupTitle,
            };
        });

        let containerHeight = 0;

        // Sort legends by height in descending order
        legendData.sort((a, b) => b.height - a.height);

        let currentX = 0;
        let currentY = 0;

        // Loop through each legend and position it in the container
        legendData.forEach((box) => {
            // Check if the current legend can fit in the remaining width of the container
            if (currentX + box.width <= containerWidth) {
                box.x = currentX;
                box.y = currentY;
                currentX += box.width;
                if (currentY + box.height > containerHeight) {
                    containerHeight = currentY + box.height;
                }
            } else {
                // Move to the next line
                currentX = 0;
                currentY = containerHeight + 25;
                if (legendPagination) {
                    currentY += 30;
                }

                box.x = currentX;
                box.y = currentY;
                currentX += box.width;
                containerHeight += box.height;
            }
            box.x += containerX;
        });

        const legendsY = this.getLegendY();
        const legends = legendData.map(({ items, height, width, x, y, itemsOffsetY }) => ({
            ...this.theme.legend,
            ...this.theme.line.legend,
            top: legendsY + y + itemsOffsetY,
            ...(this.props.getSelectedSeries && {
                selected: this.props.getSelectedSeries(this.getSeriesKeys()),
            }),
            show: withLegend,
            formatter: (name) => legendNamesMap[name],
            orient: 'vertical',
            width,
            height,
            data: items,
            left: x,
            textStyle: {
                ...this.theme.line.legend.textStyle,
                width: legendItemWidth - 30 - legendGroupGap,
                overflow: 'break',
            },
            ...(legendPagination && {
                type: 'scroll',
                animation: false,
                animationDurationUpdate: 0,
                orient: 'vertical',
            }),
        }));

        const titles = legendData.map(({ name, x, y, width, showGroupTitle }) => ({
            type: 'text',
            silent: true,
            x,
            y: legendsY + y,
            style: {
                text: showGroupTitle ? name : '',
                ...this.theme?.line.legend.titleTextStyle,
                width: width - legendGroupGap,
            },
        }));

        return { legends, titles };
    };

    getSeriesKeys = () => {
        const { columns, records } = this.props;
        const dataKeyIndex = columns.indexOf(PRIMARY_ENTITY_ID_LOCALIZATION_KEY);

        return uniq(records.map((record) => record[dataKeyIndex]));
    };

    getGridY = () => {
        const { isHeatmapEnabled, subheadingHeight } = this.props;
        const { titlesHeight, extraTitlesHeight } = this.state;
        const multiplier = isHeatmapEnabled ? 2 : 1;
        return titlesHeight + extraTitlesHeight + multiplier * TITLE_PADDING + subheadingHeight;
    };

    getLegendY = () => this.getGridY() + this.props.height + 100;

    breakDataBySeries = (): DataRecord[][] => {
        const { columns, records } = this.props;

        const { xAxisKey, groupByKeys, sortRecords } = this.props;

        if (!records.length) {
            return [];
        }

        const xAxisKeyIdx = columns.indexOf(xAxisKey);
        const groupByKeyIdxs = groupByKeys.map((key) => columns.indexOf(key));

        const minXAxisValue = records[0][xAxisKeyIdx];
        const sortedRecords = sortRecords ? records.sort(sortRecords) : records;
        const seriesData = Object.values(
            groupBy((record) => groupByKeyIdxs.map((key) => record[key]).join(','), sortedRecords)
        );

        const minValueSerieIdx = seriesData.findIndex(
            (serie) => serie[0][xAxisKeyIdx] === minXAxisValue
        );

        if (minValueSerieIdx > 0) {
            seriesData.unshift(seriesData.splice(minValueSerieIdx, 1)[0]);
        }

        return seriesData;
    };

    getEchartsUpperValueKey = () => `echarts_${this.props.areaKeys[0]}`;

    getDatasets = (data, showArea) => {
        const { columns: sourceColumns } = this.props;

        if (showArea) {
            const { areaKeys } = this.props;
            const upperIndex = sourceColumns.indexOf(areaKeys[0]);
            const lowerIndex = sourceColumns.indexOf(areaKeys[1]);
            const columns = sourceColumns.concat(this.getEchartsUpperValueKey());

            return data.map((records) => ({
                source: records.map((record) => [
                    ...record,
                    record[upperIndex] - record[lowerIndex],
                ]),
                dimensions: columns,
                sourceHeader: false,
            }));
        } else {
            return data.map((records) => ({
                source: records,
                dimensions: sourceColumns,
                sourceHeader: false,
            }));
        }
    };

    getSeries = (datasets, showArea) => {
        if (datasets.length === 0) {
            return [];
        }

        const { areaKeys, columns, generateLineParams, markLines, xAxisKey, yAxisKey } = this.props;

        const { xAxisValues } = this.state;

        const xAxisKeyIdx = columns.indexOf(xAxisKey);
        const yAxisKeyIdx = columns.indexOf(yAxisKey);

        const series = datasets.map((records, idx) => ({
            type: 'line',
            encode: {
                x: xAxisKeyIdx,
                y: yAxisKeyIdx,
            },
            datasetIndex: idx,
            name: this.getSeriesName(records[0], idx),
            symbol: 'circle',
            symbolSize: 5,
            showSymbol: xAxisValues.length <= 60,
            ...(generateLineParams && generateLineParams(records[0])),
        }));

        let upperSeries = [];
        let lowerSeries = [];
        if (series && series.length && showArea) {
            const lowerYIdx = columns.indexOf(areaKeys[1]);
            lowerSeries = series.map(({ type, datasetIndex, name }, idx) => ({
                type,
                datasetIndex,
                name,
                encode: {
                    x: xAxisKeyIdx,
                    y: lowerYIdx,
                },
                stack: `${idx}-area`,
                symbol: 'none',
                lineStyle: {
                    opacity: 0,
                },
            }));

            const columnsLength = columns.length;
            upperSeries = lowerSeries.map((record) => ({
                ...record,
                encode: {
                    x: xAxisKeyIdx,
                    y: columnsLength,
                },
                areaStyle: {
                    opacity: 0.5,
                },
            }));

            series.push(...lowerSeries);
            series.push(...upperSeries);

            const oneValueDatasets = datasets.filter((records) => records.length === 1);
            if (oneValueDatasets && oneValueDatasets.length) {
                series.push(...this.simulateOneValueSeriesAreas(oneValueDatasets));
            }
        }

        if (markLines && markLines.length) {
            series.push({
                ...series[0],
                data: [],
                name: '',
                markLine: {
                    ...this.theme.markLine,
                    data: markLines
                        .filter(
                            ({ isHorizontal, value }) =>
                                isHorizontal || (!isHorizontal && xAxisValues.includes(value))
                        )
                        .map(({ isHorizontal, label, value, color }) => {
                            const item = {};
                            if (isHorizontal) {
                                item.yAxis = value;
                                item.lineStyle = {
                                    color,
                                    opacity: 1,
                                };
                            } else {
                                item.xAxis = value;
                            }
                            if (label) {
                                item.label = {
                                    show: true,
                                    position: isHorizontal ? 'insideEndTop' : null,
                                    color,
                                    formatter: () => label,
                                };
                            }
                            return item;
                        }),
                },
            });
        }

        return series;
    };

    simulateOneValueSeriesAreas = (oneValueDatasets) => {
        const { areaKeys, columns, formatSeriesName, xAxisKey } = this.props;

        const xAxisKeyIdx = columns.indexOf(xAxisKey);

        return oneValueDatasets.map((records, idx) => {
            const record = records[0];
            const xAxisValue = record[xAxisKeyIdx];

            return {
                type: 'line',
                name: formatSeriesName ? formatSeriesName('', '', records[0]) : idx,
                data: areaKeys.map((areaKey) => [xAxisValue, record[columns.indexOf(areaKey)]]),
                symbol: 'none',
                lineStyle: {
                    opacity: 0.5,
                },
            };
        });
    };

    getChartEvents = (): Record<string, Function> => ({
        ...this.props.onEvents,
        datazoom: this.onDataZoomChange,
    });

    setCursorPosition = (event) => {
        cursorCoords.y = event.offsetY;
    };

    getChartHeight = () => {
        const { legendHeight } = this.state;

        return this.getLegendY() + SLIDER_HEIGHT + legendHeight;
    };

    render() {
        const { isLoadingData } = this.props;

        return (
            // chart-wrapper is required for resize in PDF export
            <ChartWrapper id="chart-wrapper">
                <EChartsWrapper
                    option={this.getChartOption()}
                    onEvents={this.getChartEvents()}
                    onChartReady={this.onChartReady}
                    onChartResize={this.onChartResize}
                    showLoading={isLoadingData}
                    loadingOption={echartsTheme.loading}
                    style={{ ...styles.echarts, height: this.getChartHeight() }}
                    theme={this.theme}
                />
            </ChartWrapper>
        );
    }
}

export default LineChart;
